import { useEffect, useState } from 'react'
import useOrganizationStore from '@/stores/useOrganizationStore'
import { Building, Copy, CheckCircle } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { useToast } from '@/components/ui/use-toast'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

interface ChatHeaderProps {
  organizationId: string
  sessionId: string
}

export const ChatHeader = ({ organizationId, sessionId }: ChatHeaderProps) => {
  const {
    currentOrganization: org,
    fetchOrganizationById,
    isLoading,
  } = useOrganizationStore()
  const { toast } = useToast()
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (organizationId && org?.id !== organizationId) {
      fetchOrganizationById(organizationId)
    }
  }, [organizationId, org, fetchOrganizationById])

  const handleCopy = () => {
    const sessionUrl = `${window.location.origin}/session/${sessionId}`
    navigator.clipboard.writeText(sessionUrl).then(() => {
      setCopied(true)
      toast({
        description: 'Session URL copied to clipboard!',
      })
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const organizationName = org?.id === organizationId ? org.name : null

  return (
    <div className="sticky top-16 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-10">
      <div className="container max-w-3xl mx-auto p-3 border-b">
        <div className="flex items-center justify-between gap-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-2 truncate min-w-0">
            <Building className="h-4 w-4 flex-shrink-0" />
            {isLoading && !organizationName ? (
              <Skeleton className="h-5 w-40" />
            ) : (
              <span className="truncate">
                {organizationName || 'Loading organization...'}
              </span>
            )}
            <span className="text-muted-foreground/50">/</span>
            <span className="truncate font-mono text-xs">{sessionId}</span>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={handleCopy}
                size="icon"
                variant="ghost"
                className="h-7 w-7 flex-shrink-0"
              >
                {copied ? (
                  <CheckCircle className="h-4 w-4 text-success" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
                <span className="sr-only">Copy session URL</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Copy session URL</p>
            </TooltipContent>
          </Tooltip>
        </div>
      </div>
    </div>
  )
}
